import {definedSocialClickIds} from '@commercial-website/analytics'
import type {HeaderData, HeaderNavItem, HeaderNavLink} from '@commercial-website/data-layer'
import type {Locale} from '@commercial-website/data-layer/locale'
import {Header} from '@commercial-website/ui'
import type {HeaderProps, NavItem, NavLink} from '@commercial-website/ui'
import React from 'react'

import {ctaClicked, elementClicked, ElementType} from '@/analytics/segment'
import {TrackedLocalizedLink} from '@/components/localized-link'
import {socialClickIdCookies} from '@/config/cookies'
import logoHorizontal from '@/images/icons/logo-horizontal.svg'
import {useGlobalStrings} from '@/providers/global-strings'
import {useLocale} from '@/providers/locale'

import ExternalLink from '../external-link'

const TRACKED_ELEMENT_LOCATION = 'navbar header'

type HeaderV2Props = {
    content: HeaderData
    ctaButton: HeaderProps['ctaButton']
    testId: string
    hideLeftNav?: boolean
    hideRightNav?: boolean
}

type NavLinkTrackingData = {
    linkText: string
    linkType: 'cta' | 'element'
    elementLocation: string
}

export const getLogInUrl = (locale: Locale): string => {
    return `${process.env.GATSBY_PLEO_APP_URL}/login?lang=${locale.language}`
}

// HeaderData hrefs can be absolute (Prismic "Web" link type nav items, e.g. linking out to
// blog.pleo.io) as well as internal relative paths. GatsbyLink (used by TrackedLocalizedLink) is
// documented as internal-only, so absolute hrefs must render as plain external anchors instead —
// mirroring how `PrismicLink` already dispatches Web-type links to `ExternalLink` elsewhere.
const isAbsoluteUrl = (href: string): boolean => /^https?:\/\//.test(href)

const trackNavLinkClick = (trackingData: NavLinkTrackingData, destinationUrl: string) => {
    const trackingPayload = {
        elementLocation: trackingData.elementLocation,
        elementType: ElementType.Link,
        elementText: trackingData.linkText,
        destinationUrl,
    }

    if (trackingData.linkType === 'cta') {
        ctaClicked({...trackingPayload, ...definedSocialClickIds(socialClickIdCookies)})
    } else {
        elementClicked(trackingPayload)
    }
}

const renderNavLink = ({
    href,
    target,
    className,
    onClick,
    trackingData,
    children,
    ...rest
}: {
    href: string
    target?: HeaderNavLink['target']
    className?: string
    onClick?: React.MouseEventHandler<HTMLAnchorElement>
    trackingData: NavLinkTrackingData
    children: React.ReactNode
} & Omit<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    'href' | 'target' | 'className' | 'onClick'
>) => {
    if (isAbsoluteUrl(href)) {
        const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
            trackNavLinkClick(trackingData, href)
            onClick?.(e)
        }

        // Match `PrismicLink`'s isWebLink behaviour: default to same-tab unless explicitly '_blank'.
        return (
            <ExternalLink
                {...rest}
                className={className}
                to={href}
                target={target || '_self'}
                onClick={handleClick}
            >
                {children}
            </ExternalLink>
        )
    }

    return (
        <TrackedLocalizedLink
            {...rest}
            className={className}
            rawPath
            to={href}
            target={target}
            trackingData={trackingData}
            onClick={onClick}
        >
            {children}
        </TrackedLocalizedLink>
    )
}

const mapLink = (link: HeaderNavLink): NavLink => ({
    text: link.text,
    subText: link.subText,
    render: ({children, className, onClick, ...rest}) =>
        renderNavLink({
            ...rest,
            href: link.href,
            target: link.target,
            className,
            onClick,
            trackingData: {
                linkText: link.text,
                linkType: 'element',
                elementLocation: TRACKED_ELEMENT_LOCATION,
            },
            children,
        }),
})

const mapNavItems = (items: HeaderNavItem[]): NavItem[] =>
    items.map((item) => ({
        label: item.label,
        hideOn: item.hideOn,
        render: item.href
            ? ({children, className, onClick, ...rest}) =>
                  renderNavLink({
                      ...rest,
                      href: item.href as string,
                      target: item.target,
                      className,
                      onClick,
                      trackingData: {
                          linkText: item.label,
                          linkType: 'element',
                          elementLocation: TRACKED_ELEMENT_LOCATION,
                      },
                      children,
                  })
            : undefined,
        groups: item.groups?.map((group) => ({
            label: group.label,
            links: group.links.map(mapLink),
        })),
    }))

export const HeaderV2 = ({
    content,
    ctaButton,
    testId,
    hideLeftNav,
    hideRightNav,
}: HeaderV2Props) => {
    const currentLocale = useLocale()
    const loginUrl = getLogInUrl(currentLocale)
    const {home} = useGlobalStrings()

    return (
        <Header
            logo={({className}) => (
                <TrackedLocalizedLink
                    to="/"
                    trackingData={{
                        linkText: 'logo',
                        linkType: 'element',
                        elementLocation: TRACKED_ELEMENT_LOCATION,
                    }}
                    className={className}
                >
                    <img src={logoHorizontal} alt={home} />
                </TrackedLocalizedLink>
            )}
            items={mapNavItems(content.items)}
            logIn={{
                text: content.logInText,
                render: ({className, children, onClick, ...rest}) => (
                    <ExternalLink
                        {...rest}
                        onClick={(e) => {
                            elementClicked({
                                elementLocation: TRACKED_ELEMENT_LOCATION,
                                elementType: ElementType.Link,
                                elementText: content.logInText,
                                destinationUrl: loginUrl,
                            })
                            onClick?.(e)
                        }}
                        className={className}
                        to={loginUrl}
                    >
                        {children}
                    </ExternalLink>
                ),
            }}
            ctaButton={ctaButton}
            secondaryCta={
                content.secondaryCtaText && content.secondaryCtaHref
                    ? {
                          text: content.secondaryCtaText,
                          // `target` is destructured out (unused) so Base UI's wider
                          // HTMLAttributeAnchorTarget type doesn't leak into renderNavLink's rest.
                          render: ({className, children, onClick, target: _target, ...rest}) =>
                              renderNavLink({
                                  ...rest,
                                  href: content.secondaryCtaHref as string,
                                  className,
                                  onClick,
                                  trackingData: {
                                      linkText: content.secondaryCtaText as string,
                                      linkType: 'cta',
                                      elementLocation: TRACKED_ELEMENT_LOCATION,
                                  },
                                  children,
                              }),
                      }
                    : undefined
            }
            noResultsText={content.noResultsText}
            hideLeftNav={hideLeftNav}
            hideRightNav={hideRightNav}
            testId={testId}
        />
    )
}
